import { create } from 'zustand';
import type { BotTier, CoreBand, Item, MatchResult, ThreatBand } from '@qoobd/rules';
import { challengeFromLocation, type Challenge } from './challenge';
import { DEFAULT_ARMOR, parseArmor, type ArmorChoice } from '@qoobd/rules';

export type Phase = 'intro' | 'playing' | 'over';
export type Atmosphere = 'normal' | 'subtle' | 'off';
/** Touch controls: the on-screen pad, or swipe and tap gestures only. */
export type PadMode = 'pad' | 'gestures';

export interface LockState {
  label: string;
  startedAt: number; // performance.now() time
  durationMs: number;
}

export interface ResultSummary extends MatchResult {
  playerItem: Item;
  rivalItem: Item;
  elapsedMs: number;
  steps: number;
  actions: number;
  bonks: number;
  explored: number;
  tier: BotTier;
  seed: number;
  /** Online only: the match ended because a player left. */
  forfeit?: boolean;
}

export type Mode = 'play' | 'watch' | 'online';

/** Online 1v1 lobby and match status. */
export interface OnlineStatus {
  status: 'idle' | 'connecting' | 'waiting' | 'countdown' | 'playing' | 'over' | 'error';
  /** Private room code to share, when hosting a friend. */
  code: string | null;
  error: string | null;
  /** performance.now() when the countdown ends. */
  startsAt: number;
  opponentAway: boolean;
  rematchAsked: boolean;
  rematchSent: boolean;
}

/** Spectator camera and overlay settings for AI vs AI. */
export interface WatchSettings {
  /** Agent the dolly camera rides beside. */
  follow: string;
  zoom: 'in' | 'out';
  /** Turn the camera with the watched agent (off: a fixed world view). */
  reorient: boolean;
  /** Show each agent's recent path. */
  trails: boolean;
  speed: 1 | 2;
  tiers: [BotTier, BotTier];
}

export interface SpectatorAgent {
  item: Item;
  emp: number;
  bulkhead: number;
  threat: ThreatBand;
  core: CoreBand;
  jammed: boolean;
  steps: number;
}

/** What the spectator overlay shows; refreshed a few times a second. */
export interface SpectatorView {
  agents: Record<string, SpectatorAgent>;
  /** Doors between the two agents when 3 or fewer, else null. */
  intercept: number | null;
  cloaked: boolean;
  feed: { id: number; text: string; side: string | null }[];
}

/** Everything the HUD and menus render. The 3D scene reads the controller directly instead. */
export interface GameState {
  phase: Phase;
  tier: BotTier;
  seed: number;
  coords: readonly [number, number, number];
  facing: string;
  up: string;
  item: Item;
  coreBand: CoreBand;
  threatBand: ThreatBand;
  /** Sensors show static (hit by a rival EMP). */
  jammed: boolean;
  emp: number;
  bulkhead: number;
  /** A short centred message (cloak, cache pickup, jammed). `id` changes to retrigger it. */
  toast: { text: string; sub: string; id: number } | null;
  steps: number;
  actions: number;
  bonks: number;
  startedAt: number | null;
  lock: LockState | null;
  lockFlash: number;
  bonkFlash: number;
  cloakFlash: number;
  sceneVersion: number;
  result: ResultSummary | null;
  soundOn: boolean;
  atmosphere: Atmosphere;
  padMode: PadMode;
  /** The rival is currently in view through a doorway (from this player's own view). */
  rivalVisible: boolean;
  /** performance.now() of the last cache pickup, so the HUD can wake up. */
  pickedAt: number;
  /** A challenge from a shared link (or the one being played). */
  challenge: Challenge | null;
  mode: Mode;
  watch: WatchSettings;
  spectator: SpectatorView | null;
  online: OnlineStatus;
  /** This player's armor (remembered on the device). */
  armor: ArmorChoice;
  /** The rival's armor this match; in AI vs AI, ICE's (AMBER's is in watchArmor). */
  rivalArmor: ArmorChoice;
  watchArmor: ArmorChoice;
}

/** Settings remembered on this device. Storage can be unavailable; defaults then apply. */
const PREFS_KEY = 'qoobd-prefs';
type Prefs = Pick<GameState, 'soundOn' | 'atmosphere' | 'padMode' | 'armor'>;
function loadPrefs(): Partial<Prefs> {
  try {
    const p = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') as Partial<Prefs>;
    return {
      ...(typeof p.soundOn === 'boolean' ? { soundOn: p.soundOn } : {}),
      ...(p.atmosphere === 'normal' || p.atmosphere === 'subtle' || p.atmosphere === 'off' ? { atmosphere: p.atmosphere } : {}),
      ...(p.padMode === 'pad' || p.padMode === 'gestures' ? { padMode: p.padMode } : {}),
      ...(p.armor ? { armor: parseArmor(p.armor) } : {}),
    };
  } catch {
    return {};
  }
}
export function savePrefs(): void {
  const { soundOn, atmosphere, padMode, armor } = useGame.getState();
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ soundOn, atmosphere, padMode, armor }));
  } catch {
    /* not remembered */
  }
}

export const useGame = create<GameState>(() => ({
  phase: 'intro',
  tier: 'tactical',
  seed: 0,
  coords: [0, 0, 0],
  facing: '−Z',
  up: '+Y',
  item: 'rock',
  coreBand: 'none',
  threatBand: 'none',
  jammed: false,
  emp: 1,
  bulkhead: 1,
  toast: null,
  steps: 0,
  actions: 0,
  bonks: 0,
  startedAt: null,
  lock: null,
  lockFlash: 0,
  bonkFlash: 0,
  cloakFlash: 0,
  sceneVersion: 0,
  result: null,
  soundOn: true,
  atmosphere: 'normal',
  padMode: 'pad',
  rivalVisible: false,
  pickedAt: 0,
  challenge: challengeFromLocation(),
  mode: 'play',
  watch: { follow: 'player', zoom: 'out', reorient: false, trails: true, speed: 1, tiers: ['tactical', 'relentless'] },
  spectator: null,
  armor: DEFAULT_ARMOR,
  rivalArmor: DEFAULT_ARMOR,
  watchArmor: DEFAULT_ARMOR,
  online: { status: 'idle', code: null, error: null, startsAt: 0, opponentAway: false, rematchAsked: false, rematchSent: false },
  ...loadPrefs(),
}));
